/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";

import PageLayout from "../components/layout/PageLayout";

import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import OperationPrintTransfersDetails from "components/Prints/Operations/OperationPrintTransfersDetails";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import { PrintLayout } from "components/Prints/PrintLayout";
import Loader from "components/lists/Loader";
import StockUnitSelectionModal from "components/Operations/StockUnitSelectionModal";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import ObservationRecordsModal, {
  ObservationRecordSavePayload,
} from "components/utils/ObservationRecordsModal";
import {
  ARTICLE_LOCATIONS_URL,
  ARTICLE_LOCATION_STATE_URL,
  MOVEMENT_ARTICLES_TRANSFER_ORIGIN_STOCK_UNIT_SELECTION_POOL_URL,
  MOVEMENT_ARTICLES_TRANSFER_ORIGINS_URL,
  MOVEMENTS_URL,
} from "constants/urls";
import { dateISOFormat, dateToLocalMySQLFormat } from "helpers";
import {
  buildObservationRecordItemsFromStockUnits,
  type ArticleLocationObservationRecordItem,
} from "helpers/articleLocationObservationRecords";
import {
  hasMultipleObservationValues,
  normalizeObservationInput,
} from "helpers/stockUnitObservationSelection";
import useCrud from "hooks/useCrud";
import useInfiniteApiList from "hooks/useInfiniteApiList";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useReactToPrint } from "react-to-print";
import {
  ArticleLocation,
  ArticleLocationObservationRecordStatus,
  ArticleLocationObservationRecordStockUnit,
  Location,
  Movement,
  Transfer,
  TransferOriginStockUnitSelectionPoolPayload,
  TransferOriginStockUnitSelectionPoolResponse,
  TransferOriginStockUnitSelectionPoolStockUnit,
  TransferOriginRow,
  UpdateArticleLocationObservationRecordsPayload,
} from "types";
import {
  DATE_VIEWS,
  MovementTypes,
  STATUS_CODES,
  defaultMovement,
} from "../constants";
import TransferLocationTable from "components/Transfer/TransferLocationTable";
import TransferNewLocation from "components/Transfer/TransferNewLocation";
import CustomNumberInput from "components/CustomNumberInput";
import { useConfirmationModal } from "hooks/useConfirmationModal";
import {
  ArticleLocationRow,
  TransferOriginFilters,
} from "components/Transfer/TransferLocationTable/types";

export enum DestinationTypes {
  Existing = 1,
  New = 2,
}

export type SelectedArticleLocations = {
  from: ArticleLocation | null;
  to: ArticleLocation | null;
};

const defaultTransferOriginFilters: TransferOriginFilters = {
  locationId: undefined,
  articleCode: "",
  lote: "",
  oc: "",
};

const resolveTransferObservationRecordStatus = (
  row?: ArticleLocation | null,
): ArticleLocationObservationRecordStatus | null => {
  if (!row?.status) {
    return null;
  }

  if (row.status === "reserved") {
    return "reserved";
  }

  if (row.status === "available") {
    return "available";
  }

  if (row.status === "unavailable" || row.status === "not_available") {
    return "not_available";
  }

  return null;
};

const resolveTransferSelectionOriginKind = (
  row?: ArticleLocation | null,
): "available" | "not_available" | "reserved" | null => {
  if (!row) {
    return null;
  }

  if (row.origin_kind === "reserved" || row.status === "reserved") {
    return "reserved";
  }

  if (
    row.origin_kind === "unavailable" ||
    row.status === "unavailable"
  ) {
    return "not_available";
  }

  return "available";
};

export default function TransferPage() {
  const [movement, setMovement] = useState<Movement>({
    ...defaultMovement,
    movement_type_id: MovementTypes.Transfer,
  });
  const [transferReportMovements, setTransferReportMovements] = useState<Movement[]>([]);
  const [lastTransferOrigin, setLastTransferOrigin] = useState<ArticleLocationRow | null>(null);
  const [selectedTransferOrigins, setSelectedTransferOrigins] = useState<ArticleLocation[]>([]);
  const isTransferSheetOpen = transferReportMovements.length > 0;
  const [transferSheetMovementsToPrint, setTransferSheetMovementsToPrint] = useState<Movement[]>([]);

  const [transferStock, setTransferStock] = useState<{
    isTotal: boolean | null;
    value: number;
  }>({
    isTotal: null,
    value: 0,
  });
  const [clientDocumentInput, setClientDocumentInput] = useState(
    defaultMovement.client_document ?? "",
  );
  const [numeroRemitoInput, setNumeroRemitoInput] = useState(
    defaultMovement.numero_remito ?? "",
  );
  const [movementIdInput, setMovementIdInput] = useState("");
  const [originFilters, setOriginFilters] =
    useState<TransferOriginFilters>(defaultTransferOriginFilters);
  const [selectedObservationRow, setSelectedObservationRow] =
    useState<ArticleLocation | null>(null);
  const [observationRecordsOpen, setObservationRecordsOpen] = useState(false);
  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState(false);
  const [observationRecords, setObservationRecords] = useState<
    ArticleLocationObservationRecordItem[]
  >([]);
  const [savingObservationRecordId, setSavingObservationRecordId] = useState<
    string | null
  >(null);
  const [transferStockUnitSelectionPoolOpen, setTransferStockUnitSelectionPoolOpen] =
    useState(false);
  const [transferStockUnitSelectionPoolLoading, setTransferStockUnitSelectionPoolLoading] =
    useState(false);
  const [transferStockUnitSelectionPool, setTransferStockUnitSelectionPool] =
    useState<TransferOriginStockUnitSelectionPoolStockUnit[]>([]);
  const { showModal } = useConfirmationModal();

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforePrint: () => {},
    onAfterPrint: async () => {
      resetTransfer();
      setTransferReportMovements([]);
      setTransferSheetMovementsToPrint([]);
      setLastTransferOrigin(null);
      await getArticleLocations();
    },
  });

  const [movementLoading, setMovementLoading] = useState<boolean>(false);

  const {
    loading: articleLocationLoading,
    firstLoad: articleLocationsFirstLoad,
    data: articleLocations,
    fetchData: getArticleLocations,
    setData: setArticleLocations,
  } = useCrud<ArticleLocation>(`${ARTICLE_LOCATIONS_URL}expanded`);
  const {
    fetchData: getTransferOrigins,
  } = useCrud<TransferOriginRow>(MOVEMENT_ARTICLES_TRANSFER_ORIGINS_URL, {
    disableAutoFetch: true,
  });
  const { updateData: updateArticleLocationState } = useCrud<{
    target_status: "available" | "not_available";
    quantity: number;
  }>(ARTICLE_LOCATION_STATE_URL, {
    disableAutoFetch: true,
  });
  const { data: movementById, fetchData: getMovementById } = useCrud<Movement>(
    MOVEMENTS_URL,
    { disableAutoFetch: true }
  );
  const { fetchData: getObservationRecordStockUnits } =
    useCrud<ArticleLocationObservationRecordStockUnit>(ARTICLE_LOCATIONS_URL, {
      disableAutoFetch: true,
    });
  const { updateData: updateObservationRecordStockUnits } =
    useCrud<UpdateArticleLocationObservationRecordsPayload>(ARTICLE_LOCATIONS_URL, {
      disableAutoFetch: true,
    });
  const { createData: createTransferOriginStockUnitSelectionPool } =
    useCrud<TransferOriginStockUnitSelectionPoolPayload>(
      MOVEMENT_ARTICLES_TRANSFER_ORIGIN_STOCK_UNIT_SELECTION_POOL_URL,
      {
        disableAutoFetch: true,
      },
    );
  const { data: createdMovements, createData: createMovement } = useCrud<
    Movement[]
  >(MOVEMENTS_URL, {
    disableAutoFetch: true,
  });

  const { userData, stores, clients, locations, users } = useSession();
  const { showSnackBar } = useToast();

  const [selectedStoreLocations, setSelectedStoreLocations] =
    useState<Location[]>(locations);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === movement.client_id) ?? null,
    [clients, movement.client_id]
  );

  const transferOriginFilters = useMemo(
    () => ({
      clientId: movement.client_id,
      movementId: movement.movement_id,
      ...originFilters,
    }),
    [movement.client_id, movement.movement_id, originFilters],
  );

  const fetchTransferOriginsPage = useCallback(
    ({
      page,
      limit,
      search,
      filters,
    }: {
      page: number;
      limit: number;
      search: string;
      filters: typeof transferOriginFilters;
    }) => {
      const query = new URLSearchParams({
        clientId: String(filters.clientId),
        page: String(page),
        limit: String(limit),
      });

      if (filters.movementId) {
        query.set("movementId", String(filters.movementId));
      }

      if (filters.locationId) {
        query.set("locationId", String(filters.locationId));
      }

      if (filters.articleCode) {
        query.set("articleCode", filters.articleCode);
      }

      if (filters.lote) {
        query.set("lote", filters.lote);
      }

      if (filters.oc) {
        query.set("oc", filters.oc);
      }

      if (search.trim()) {
        query.set("search", search.trim());
      }

      return getTransferOrigins(`?${query.toString()}`, true, {}, true);
    },
    [getTransferOrigins],
  );

  const {
    items: transferOriginRows,
    loading: transferOriginsLoading,
    loadingMore: transferOriginsLoadingMore,
    hasMore: transferOriginsHasMore,
    reset: resetTransferOrigins,
    loadFirstPage: reloadTransferOrigins,
    loadNextPage: loadNextTransferOriginsPage,
  } = useInfiniteApiList<TransferOriginRow, typeof transferOriginFilters>({
    enabled: Boolean(movement.client_id),
    limit: 10,
    filters: transferOriginFilters,
    getItemKey: (item) => item.row_key,
    fetchPage: fetchTransferOriginsPage,
  });

  const handleChangeMovement = useCallback((
    name: string,
    value: string | number | Transfer | Transfer[] | null
  ) => {
    setMovement((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectedTransferOriginsChange = useCallback((origins: ArticleLocation[]) => {
    const originIds = new Set(origins.map((origin) => origin.id));

    setSelectedTransferOrigins(origins);
    setMovement((prev) => ({
      ...prev,
      transfer:
        prev.transfer?.from && !originIds.has(prev.transfer.from.id)
          ? undefined
          : prev.transfer,
      transfers: (prev.transfers ?? []).filter(
        (item) => item.from?.id && originIds.has(item.from.id),
      ),
    }));
  }, []);

  const handleClientDocumentBlur = useCallback(() => {
    handleChangeMovement("client_document", clientDocumentInput);
  }, [clientDocumentInput, handleChangeMovement]);

  const handleNumeroRemitoBlur = useCallback(() => {
    handleChangeMovement("numero_remito", numeroRemitoInput);
  }, [handleChangeMovement, numeroRemitoInput]);

  const resetTransfer = () => {
    setTransferStock({ isTotal: null, value: 0 });
    setOriginFilters(defaultTransferOriginFilters);
    resetTransferOrigins();
    setSelectedTransferOrigins([]);
    setTransferStockUnitSelectionPool([]);
    setTransferStockUnitSelectionPoolOpen(false);
    setTransferStockUnitSelectionPoolLoading(false);
    setMovement({
      ...defaultMovement,
      created_by: movement.created_by,
      movement_type_id: MovementTypes.Transfer,
      transfers: [],
    });
  };

  const resetTransferOperationData = async () => {
    setTransferStock({ isTotal: null, value: 0 });
    setSelectedTransferOrigins([]);
    setTransferStockUnitSelectionPool([]);
    setTransferStockUnitSelectionPoolOpen(false);
    setTransferStockUnitSelectionPoolLoading(false);

setMovement((prev) => ({
    ...defaultMovement,
    movement_type_id: MovementTypes.Transfer,
    date: dateToLocalMySQLFormat(prev.date),
    client_id: prev.client_id,
    associated_client: prev.associated_client,
    client_document: prev.client_document,
    numero_remito: prev.numero_remito,
    observation: prev.observation,
    created_by: prev.created_by,
    updated_by: prev.updated_by,
    is_billable: prev.is_billable,
    store_id: prev.store_id,
    weight: prev.weight,
    pallets: prev.pallets,
    id: prev.id,
    movement_id: prev.movement_id ?? null,
    articles: [],
    additional_items: [],
    containers: [],
    transfer: undefined,
    transfers: [],
    }));

    if (movement.client_id) {
      const query = new URLSearchParams({
        clientId: String(movement.client_id),
      });

      if (movement.movement_id) {
        query.set("movementId", String(movement.movement_id));
      }

      await Promise.all([getArticleLocations(`?${query.toString()}`, true), reloadTransferOrigins()]);
    }
  };

  const loadTransferStockUnitSelectionPool = useCallback(async () => {
    if (!movement.client_id) {
      showSnackBar("Debe seleccionar un cliente.", "error");
      return null;
    }

    const transfer = movement.transfer;

    if (!transfer?.from) {
      showSnackBar(
        "Debe seleccionar un origen para elegir las unidades a transferir.",
        "error",
      );
      return null;
    }

    if (!transfer.amount || transfer.amount <= 0) {
      showSnackBar(
        "Debe seleccionar una cantidad valida para elegir las unidades a transferir.",
        "error",
      );
      return null;
    }

    const originKind = resolveTransferSelectionOriginKind(transfer.from);

    if (!originKind) {
      showSnackBar(
        "No se pudo resolver el tipo de origen para la transferencia.",
        "error",
      );
      return null;
    }

    setTransferStockUnitSelectionPoolLoading(true);

    const response = await createTransferOriginStockUnitSelectionPool(
      {
        client_id: movement.client_id,
        article_location_id: Number(transfer.from.id),
        origin_kind: originKind,
        amount: Number(transfer.amount),
        reserved_movement_id:
          originKind === "reserved"
            ? Number(transfer.from.reserved_movement_id ?? 0) || null
            : null,
      },
      true,
      false,
    );

    setTransferStockUnitSelectionPoolLoading(false);

    if (response.status !== STATUS_CODES.OK) {
      showSnackBar(
        response.data?.message ??
          "No se pudieron obtener las unidades para transferir.",
        "error",
      );
      return null;
    }

    const nextTransferStockUnitSelectionPool =
      (response.data as TransferOriginStockUnitSelectionPoolResponse | undefined)
        ?.stock_units ?? [];

    setTransferStockUnitSelectionPool(nextTransferStockUnitSelectionPool);
    return nextTransferStockUnitSelectionPool;
  }, [
    createTransferOriginStockUnitSelectionPool,
    movement.client_id,
    movement.transfer,
    showSnackBar,
  ]);

  const submitTransferMovement = useCallback(
    async (transfer?: Transfer) => {
      setMovementLoading(true);
      const transferPayloads = movement.transfers?.length
        ? Object.values(
            movement.transfers.reduce<Record<string, Transfer>>((acc, item) => {
              const originKey = [
                item.from?.id,
                item.from?.origin_kind ?? item.from?.status ?? "available",
                item.from?.reserved_movement_id ?? "",
              ].join("-");
              const current = acc[originKey];

              if (!current) {
                acc[originKey] = {
                  ...item,
                  amount: Number(item.amount ?? 0),
                  destinations: item.destinations ?? [],
                  user_id: movement.created_by,
                };
                return acc;
              }

              current.amount =
                Number(current.amount ?? 0) + Number(item.amount ?? 0);
              current.destinations = [
                ...(current.destinations ?? []),
                ...(item.destinations ?? []),
              ];

              return acc;
            }, {}),
          )
        : undefined;
      const transferPayload = transferPayloads?.length
        ? undefined
        : transfer ?? movement.transfer;

      const response = await createMovement(
        [
          {
            ...movement,
            id: transferReportMovements[0]?.id ?? movement.id,
            store_id: movement.store_id === 0 ? null : movement.store_id,
            transfer: transferPayload
              ? {
                  from: transferPayload.from,
                  to: transferPayload.to,
                  amount: transferPayload.amount,
                  new_location_id: transferPayload.new_location_id,
                  destinations: transferPayload.destinations,
                  user_id: movement.created_by,
                  stock_unit_ids:
                    transferPayload.stock_unit_ids?.length
                      ? transferPayload.stock_unit_ids
                      : undefined,
                }
              : undefined,
            transfers: transferPayloads,
          },
        ],
        true
      );

      if (response.status === STATUS_CODES.OK) {
        const aggregatedMovement = await getMovementById(`${response.data[0].id}`, true);
        const createdTransfer = aggregatedMovement.data[0];
        const nextReportMovements = [createdTransfer];

        setTransferReportMovements(nextReportMovements);
        setMovementLoading(false);
        showSnackBar("La transferencia se ha realizado exitosamente", "success");

        showModal({
          title: "Nueva transferencia",
          message: "¿Desea cargar otra transferencia manteniendo los datos de cabecera?",
          action: resetTransferOperationData,
          cancelAction: async () => {
            setTransferSheetMovementsToPrint(nextReportMovements);
          },
        });
      } else {
        setMovementLoading(false);
        showSnackBar(
          response.data?.message ||
            "No se ha podido realizar la transferencia, por favor intente nuevamente",
          "error"
        );
      }
    },
    [
      createMovement,
      getMovementById,
      movement,
      resetTransferOperationData,
      showModal,
      showSnackBar,
      transferReportMovements,
    ]
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const hasMultipleTransfers = (movement.transfers?.length ?? 0) > 0;

    const currentTransfer = movement.transfer;

    if (!currentTransfer && !hasMultipleTransfers) {
      return;
    }

    if (hasMultipleTransfers) {
      await submitTransferMovement();
      return;
    }

    const transferStockUnits = await loadTransferStockUnitSelectionPool();

    if (!transferStockUnits) {
      return;
    }

    if (!currentTransfer) {
      return;
    }

    const hasExactTransferSelection =
      (currentTransfer.stock_unit_ids?.length ?? 0) === currentTransfer.amount;

    if (
      hasMultipleObservationValues(transferStockUnits) &&
      !hasExactTransferSelection
    ) {
      setTransferStockUnitSelectionPoolOpen(true);
      return;
    }

    await submitTransferMovement(currentTransfer);
  };

  const fetchObservationRecordStockUnits = useCallback(
    async (row: ArticleLocation) => {
      const status = resolveTransferObservationRecordStatus(row);

      if (!status) {
        throw new Error("La fila seleccionada no tiene un estado válido.");
      }

      const params: Record<string, string | number> = { status };

      if (status === "reserved") {
        if (!Number(row.reserved_movement_id ?? 0)) {
          throw new Error(
            "La fila reservada seleccionada no tiene reserved_movement_id.",
          );
        }

        params.reserved_movement_id = Number(row.reserved_movement_id);
      }

      const response = await getObservationRecordStockUnits(
        `${row.id}/observation-records`,
        true,
        params,
      );

      if (response.status !== STATUS_CODES.OK) {
        throw new Error(
          response.data?.message ??
            "No se pudieron obtener las observaciones de la transferencia.",
        );
      }

      return (response.data ??
        []) as ArticleLocationObservationRecordStockUnit[];
    },
    [getObservationRecordStockUnits],
  );

  const handleOpenObservationRecords = useCallback(
    async (row: ArticleLocation) => {
      setSelectedObservationRow(row);
      setObservationRecordsOpen(true);
      setObservationRecordsLoading(true);

      try {
        const stockUnits = await fetchObservationRecordStockUnits(row);
        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(stockUnits),
        );
      } catch (error) {
        console.error(error);
        setObservationRecords([]);
        showSnackBar(
          error instanceof Error
            ? error.message
            : "No se pudieron cargar las observaciones.",
          "error",
        );
      } finally {
        setObservationRecordsLoading(false);
      }
    },
    [fetchObservationRecordStockUnits, showSnackBar],
  );

  const handleCloseObservationRecords = useCallback(() => {
    setObservationRecordsOpen(false);
    setSelectedObservationRow(null);
    setObservationRecords([]);
    setObservationRecordsLoading(false);
    setSavingObservationRecordId(null);
  }, []);

  const handleSaveObservationRecord = useCallback(
    async ({
      recordId,
      observation,
      quantityMode,
      specificQuantity,
    }: ObservationRecordSavePayload) => {
      if (!selectedObservationRow) {
        return;
      }

      const status = resolveTransferObservationRecordStatus(selectedObservationRow);

      if (!status || status === "in_work_orders") {
        showSnackBar("La fila seleccionada no tiene un estado válido.", "error");
        return;
      }

      const targetRecord = observationRecords.find(
        (record) => record.id === recordId,
      );

      if (!targetRecord) {
        showSnackBar(
          "No se encontró el grupo de observaciones seleccionado.",
          "error",
        );
        return;
      }

      const targetQuantity =
        quantityMode === "specific"
          ? Number(specificQuantity ?? 0)
          : targetRecord.stock_unit_ids.length;

      if (
        !Number.isFinite(targetQuantity) ||
        targetQuantity <= 0 ||
        targetQuantity > targetRecord.stock_unit_ids.length
      ) {
        showSnackBar("La cantidad específica seleccionada no es válida.", "error");
        return;
      }

      if (
        status === "reserved" &&
        !Number(selectedObservationRow.reserved_movement_id ?? 0)
      ) {
        showSnackBar(
          "La fila reservada seleccionada no tiene reserved_movement_id.",
          "error",
        );
        return;
      }

      setSavingObservationRecordId(recordId);

      try {
        const response = await updateObservationRecordStockUnits(
          `${selectedObservationRow.id}/observation-records`,
          {
            status,
            stock_unit_ids: targetRecord.stock_unit_ids.slice(0, targetQuantity),
            observation: normalizeObservationInput(observation),
            reserved_movement_id:
              status === "reserved"
                ? Number(selectedObservationRow.reserved_movement_id)
                : null,
          },
          true,
          false,
        );

        if (response.status !== STATUS_CODES.OK) {
          throw new Error(
            response.data?.message ??
              "No se pudieron actualizar las observaciones de la transferencia.",
          );
        }

        const updatedStockUnits = (response.data ??
          []) as ArticleLocationObservationRecordStockUnit[];

        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(updatedStockUnits),
        );
        setTransferStockUnitSelectionPool([]);
        setMovement((prev) => {
          if (!prev.transfer || !selectedObservationRow) {
            return prev;
          }

          const currentStatus = resolveTransferSelectionOriginKind(prev.transfer.from);
          const selectedStatus =
            resolveTransferSelectionOriginKind(selectedObservationRow);

          if (
            prev.transfer.from?.id !== selectedObservationRow.id ||
            currentStatus !== selectedStatus
          ) {
            return prev;
          }

          return {
            ...prev,
            transfer: {
              ...prev.transfer,
              stock_unit_ids: undefined,
            },
          };
        });

        showSnackBar("Se actualizaron las observaciones correctamente.", "success");
      } catch (error) {
        console.error(error);
        showSnackBar(
          error instanceof Error
            ? error.message
            : "No se pudieron actualizar las observaciones.",
          "error",
        );
      } finally {
        setSavingObservationRecordId(null);
      }
    },
    [
      observationRecords,
      selectedObservationRow,
      showSnackBar,
      updateObservationRecordStockUnits,
    ],
  );

  const handleArticleLocationStateChange = useCallback(
    async ({
      id,
      targetStatus,
      quantity,
    }: {
      id: number;
      targetStatus: "available" | "unavailable";
      quantity: number;
    }) => {
      const resp = await updateArticleLocationState(
        id,
        {
          target_status:
            targetStatus === "available" ? "available" : "not_available",
          quantity,
        } as any,
        true,
        false
      );

      if (resp.status === STATUS_CODES.OK) {
        const updated = resp.data as Partial<ArticleLocation>;

        setArticleLocations((prev) =>
          prev.map((row) =>
            row.id === id
              ? {
                  ...row,
                  stock: Number(updated.stock ?? row.stock),
                  reserved: Number(updated.reserved ?? row.reserved),
                  not_available: Number(
                    updated.not_available ?? row.not_available ?? 0
                  ),
                  in_work_orders: Number(
                    updated.in_work_orders ?? row.in_work_orders ?? 0
                  ),
                }
              : row
          )
        );
        setSelectedTransferOrigins((prev) =>
          prev.filter((origin) => origin.id !== id)
        );

        setMovement((prev) => {
          const shouldResetSelection =
            prev.transfer?.from?.id === id || prev.transfer?.to?.id === id;

          if (!shouldResetSelection) {
            return {
              ...prev,
              transfers: (prev.transfers ?? []).filter(
                (item) => item.from?.id !== id
              ),
            };
          }

          return {
            ...prev,
            transfer: {
              ...prev.transfer,
              from: null,
              to: null,
              amount: 0,
              new_location_id: null,
            },
            transfers: (prev.transfers ?? []).filter(
              (item) => item.from?.id !== id
            ),
          };
        });

        setTransferStock({ isTotal: null, value: 0 });

        if (movement.client_id) {
          await reloadTransferOrigins();
        }

        showSnackBar("Estado de mercaderia actualizado correctamente", "success");
        return true;
      }

      showSnackBar(
        resp.data?.message ||
          "No se pudo actualizar el estado de la mercaderia",
        "error"
      );
      return false;
    },
    [
      reloadTransferOrigins,
      movement.client_id,
      setArticleLocations,
      showSnackBar,
      updateArticleLocationState,
    ]
  );

  const applyMovementIdFilter = useCallback(() => {
    const value = movementIdInput.trim();
    const movementId = value ? Number(value) : null;

    if (value && !Number.isFinite(movementId)) {
      return;
    }

    setMovement((prev) => ({
      ...prev,
      movement_id: movementId,
      transfer: undefined,
      transfers: [],
    }));

    setSelectedTransferOrigins([]);
    setTransferStock({ isTotal: null, value: 0 });
  }, [movementIdInput]);

  const handleOriginFiltersChange = useCallback((nextFilters: TransferOriginFilters) => {
    setOriginFilters(nextFilters);
  }, []);

  const isSubmitDisabled = useMemo(() => {
    const transfers = movement.transfers ?? [];

    if (transfers.length > 0) {
      const amountByOrigin = transfers.reduce<Record<number, number>>(
        (acc, item) => {
          const originId = Number(item.from?.id ?? 0);
          if (!originId) return acc;

          acc[originId] = (acc[originId] ?? 0) + Number(item.amount ?? 0);
          return acc;
        },
        {},
      );

      if (transferStockUnitSelectionPoolLoading) return true;
      if (transfers.some((item) => !item.from)) return true;
      if (transfers.some((item) => !item.destinations?.[0]?.location_id)) return true;
      if (transfers.some((item) => Number(item.amount ?? 0) <= 0)) return true;
      if (
        transfers.some((item) => {
          const originId = Number(item.from?.id ?? 0);
          const stock = Number(item.from?.stock ?? 0);

          return originId > 0 && amountByOrigin[originId] > stock;
        })
      ) {
        return true;
      }

      return false;
    }

    const t = movement.transfer;
    if (!t) return true;
    if (!t.from) return true;
    if (transferStockUnitSelectionPoolLoading) return true;

    const destinations = t.destinations ?? [];

    if (destinations.length > 0) {
      const totalDestinationAmount = destinations.reduce(
        (sum, destination) => sum + Number(destination.amount ?? 0),
        0,
      );

      if (destinations.some((destination) => !destination.location_id)) return true;
      if (destinations.some((destination) => Number(destination.amount ?? 0) <= 0)) return true;
      if (totalDestinationAmount !== Number(t.amount ?? 0)) return true;

      return false;
    }

    if (!t.new_location_id) return true;
    if (!t.amount || t.amount <= 0) return true;

    return false;
  }, [movement.transfer, movement.transfers, transferStockUnitSelectionPoolLoading]);

  useEffect(() => {
    if (movement.store_id === 0) {
      setSelectedStoreLocations(locations);
    } else if (movement.store_id) {
      setSelectedStoreLocations(
        locations.filter((l) => l.store_id === movement.store_id)
      );
    } else {
      setSelectedStoreLocations([]);
    }
  }, [movement.store_id, locations]);

  useEffect(() => {
    if (!userData) return;
    setMovement((prev) => ({ ...prev, created_by: userData.id }));
  }, [userData]);

  useEffect(() => {
    setClientDocumentInput(movement.client_document ?? "");
  }, [movement.client_document]);

  useEffect(() => {
    setNumeroRemitoInput(movement.numero_remito ?? "");
  }, [movement.numero_remito]);

  useEffect(() => {
    setMovement((prev) => {
      if (!prev.transfer) return prev;
      return {
        ...prev,
        transfer: {
          ...prev.transfer,
          amount: transferStock.value,
          stock_unit_ids: undefined,
        },
      };
    });
    setTransferStockUnitSelectionPool([]);
    setTransferStockUnitSelectionPoolOpen(false);
  }, [transferStock.value]);

  useEffect(() => {
    if (movement.client_id) {
      const query = new URLSearchParams({
        clientId: String(movement.client_id),
      });

      if (movement.movement_id) {
        query.set("movementId", String(movement.movement_id));
      }

      getArticleLocations(`?${query.toString()}`);
    } else {
      resetTransferOrigins();
    }
  }, [movement.client_id, movement.movement_id]);

  useEffect(() => {
    if (transferSheetMovementsToPrint.length > 0) {
      handlePrint();
    }
  }, [transferSheetMovementsToPrint]);


  return (
    <PageLayout
      pageTitle="Transferencia"
      pageDesc="Orden de transferencia de mercadería"
    >
      {transferSheetMovementsToPrint.length > 0 && (
        <PrintLayout
          ref={printRef}
          headerInfo={
            <OperationPrintHeaderDetails
              movement={transferSheetMovementsToPrint[0]}
              stores={stores}
              movementNumberOverride={transferSheetMovementsToPrint
                .map((movement) => movement.id)
                .filter(Boolean)
                .join(", ")}
            />
          }
        >
          {transferSheetMovementsToPrint.length === 1 ? (
            <OperationPrintDetails
              movement={transferSheetMovementsToPrint[0]}
              client={transferSheetMovementsToPrint[0].client || null}
              users={users}
            />
          ) : (
            <OperationPrintTransfersDetails movements={transferSheetMovementsToPrint} users={users} />
          )}
        </PrintLayout>
      )}
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
      >
        {movementLoading ? (
          <Loader isLoading={true} isFirstLoad={articleLocationsFirstLoad} />
        ) : (
          <Grid container columnSpacing={1}>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="user"
                  autoComplete
                  disabled
                  value={{
                    id: movement.created_by,
                    label: `${
                      users.find((u) => u.id === movement.created_by)
                        ?.first_name ?? ""
                    } ${
                      users.find((u) => u.id === movement.created_by)
                        ?.last_name ?? ""
                    }`,
                  }}
                  options={users?.map((u) => ({
                    id: u.id,
                    label: `${u.first_name} ${u.last_name}`,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(e, value: { id: number; label: string } | null) =>
                    handleChangeMovement("created_by", value?.id ?? null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label="Usuario" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="author"
                  autoComplete
                  value={{
                    id: movement.updated_by,
                    label: `${
                      users.find((u) => u.id === movement.updated_by)
                        ?.first_name ?? ""
                    } ${
                      users.find((u) => u.id === movement.updated_by)
                        ?.last_name ?? ""
                    }`,
                  }}
                  options={users?.map((u) => ({
                    id: u.id,
                    label: `${u.first_name} ${u.last_name}`,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={isTransferSheetOpen || !movement.created_by}
                  onChange={(e, value: { id: number; label: string } | null) =>
                    handleChangeMovement("updated_by", value?.id ?? null)
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label="Preparó" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <DateTimePicker
                  disableFuture
                  disabled={isTransferSheetOpen}
                  label="Fecha"
                  value={movement.date ? new Date(movement.date) : null}
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slots={{
                    actionBar: DateTimePickerActionBar,
                  }}
                  onChange={(value) => {
                    handleChangeMovement("date", dateISOFormat(value?.toISOString() ?? ""));
                    handleChangeMovement("settlement_date", dateISOFormat(value?.toISOString() ?? ""));
                  }}
                  views={DATE_VIEWS}
                  slotProps={{
                    textField: {
                      required: true,
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="client"
                  disabled={isTransferSheetOpen}
                  autoComplete
                  options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
                    id: c.id,
                    label: `${c.code} - ${c.razon_social}`,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    selectedClient
                      ? {
                          id: selectedClient.id,
                          label: selectedClient.razon_social,
                        }
                      : null
                  }
                  onChange={(e, value) => {
                    setLastTransferOrigin(null);
                    setSelectedTransferOrigins([]);
                    setTransferStock({ isTotal: null, value: 0 });
                    setTransferStockUnitSelectionPool([]);
                    setTransferStockUnitSelectionPoolOpen(false);
                    setOriginFilters(defaultTransferOriginFilters);

                    setMovement((prev) => ({
                      ...prev,
                      client_id: value?.id ?? 0,
                      associated_client: "",
                      transfer: undefined,
                      transfers: [],
                      store_id: 0,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} required label="Cliente" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth required margin="none">
                <CustomNumberInput
                  label="Número de Planilla"
                  disabled={isTransferSheetOpen}
                  value={clientDocumentInput}
                  onChange={(e) => setClientDocumentInput(e.target.value)}
                  onBlur={handleClientDocumentBlur}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Número de Remito"
                  disabled={isTransferSheetOpen}
                  value={numeroRemitoInput}
                  onChange={(e) => setNumeroRemitoInput(e.target.value)}
                  onBlur={handleNumeroRemitoBlur}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <TextField
                  label="Numero de Ingreso"
                  type="text"
                  fullWidth
                  value={movementIdInput}
                  onChange={(e) => setMovementIdInput(e.target.value)}
                  onBlur={applyMovementIdFilter}
                  disabled={isTransferSheetOpen}
                />
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="observations"
                label="Observaciones"
                type="text"
                fullWidth
                value={movement.observation}
                onChange={(e) =>
                  handleChangeMovement("observation", e.target.value)
                }
                disabled={isTransferSheetOpen}
              />
            </Grid>
            <Grid item xs={12}>
              <TransferLocationTable
                tableType={"from"}
                articleLocations={articleLocations}
                transferOriginRows={transferOriginRows}
                originFilters={originFilters}
                onOriginFiltersChange={handleOriginFiltersChange}
                filtersDisabled={
                  !selectedClient || articleLocationLoading || transferOriginsLoading
                }
                onLoadMore={loadNextTransferOriginsPage}
                hasMore={transferOriginsHasMore}
                loading={articleLocationLoading || transferOriginsLoading}
                loadingMore={transferOriginsLoadingMore}
                transferStock={transferStock}
                setTransferStock={setTransferStock}
                transfer={movement.transfer}
                selectedOrigins={selectedTransferOrigins}
                handleChangeMovement={handleChangeMovement}
                client={selectedClient ?? undefined}
                onOpenObservationRecords={handleOpenObservationRecords}
                onRequestChangeState={handleArticleLocationStateChange}
                preferredOrigin={lastTransferOrigin}
                onFromRowSelected={setLastTransferOrigin}
                onSelectedOriginsChange={handleSelectedTransferOriginsChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TransferNewLocation
                stores={stores}
                locations={selectedStoreLocations}
                articleLocations={articleLocations}
                handleChange={handleChangeMovement}
                storeId={movement.store_id}
                transfer={movement.transfer}
                transfers={movement.transfers}
                selectedOrigins={selectedTransferOrigins}
              />
            </Grid>
            <Grid item xs={12} mt={4}>
              <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
                Transferir
              </Button>
            </Grid>
          </Grid>
        )}
      </form>
      <ObservationRecordsModal
        open={observationRecordsOpen}
        loading={observationRecordsLoading}
        savingRecordId={savingObservationRecordId}
        title="Observaciones"
        records={observationRecords}
        onClose={handleCloseObservationRecords}
        onSaveRecord={handleSaveObservationRecord}
      />
      <StockUnitSelectionModal
        open={transferStockUnitSelectionPoolOpen}
        amount={movement.transfer?.amount ?? 0}
        stockUnits={transferStockUnitSelectionPool}
        title="Seleccionar unidades a transferir"
        subtitle="Elegí cuantas unidades transferir por cada observacion disponible en el origen seleccionado."
        quantityLabel="Cantidad a transferir"
        availabilityLabel="Disponible para esta observacion"
        confirmLabel="Confirmar seleccion"
        onClose={() => setTransferStockUnitSelectionPoolOpen(false)}
        onConfirm={(selectedStockUnitIds) => {
          const nextTransfer = movement.transfer
            ? {
                ...movement.transfer,
                stock_unit_ids: selectedStockUnitIds,
              }
            : null;

          setMovement((prev) => {
            if (!prev.transfer) {
              return prev;
            }

            return {
              ...prev,
              transfer: {
                ...prev.transfer,
                stock_unit_ids: selectedStockUnitIds,
              },
            };
          });
          setTransferStockUnitSelectionPoolOpen(false);

          if (nextTransfer) {
            void submitTransferMovement(nextTransfer);
          }
        }}
      />

    </PageLayout>
  );
}
